import { DOCUMENT } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, Inject, Input, OnDestroy, Renderer2 } from '@angular/core';

@Component({
  selector: 'app-scroll-to-top-button',
  standalone: false,
  template: `
    <button
      *ngIf="isVisible"
      type="button"
      class="fixed bottom-6 z-[9999] inline-flex h-11 w-11 items-center justify-center rounded-full bg-primary text-white shadow-lg transition hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-primary/60"
      [class.left-6]="direction === 'bottom'"
      [class.right-6]="direction === 'top'"
      [attr.title]="direction === 'top' ? 'Scroll to top' : 'Scroll to bottom'"
      [attr.aria-label]="direction === 'top' ? 'Scroll to top' : 'Scroll to bottom'"
      (click)="scrollToEnd()">
      <i class="fas text-sm" [class.fa-arrow-up]="direction === 'top'" [class.fa-arrow-down]="direction === 'bottom'"></i>
    </button>
  `,
  styles: [`
    :host {
      display: contents;
    }
    :host button {
      box-shadow: 0 10px 25px rgba(15, 23, 42, 0.18);
    }
  `]
})
export class ScrollToTopButtonComponent implements AfterViewInit, OnDestroy {
  /** When set, scroll this element; otherwise use the main layout scroll region if present. */
  @Input() targetId?: string;
  /** Pixels from top (top mode) or from bottom (bottom mode) before the button appears. */
  @Input() threshold = 300;
  /** `top`: jump to top when user has scrolled down. `bottom`: jump to bottom when not near the end. */
  @Input() direction: 'top' | 'bottom' = 'top';

  private static readonly mainScrollId = 'app-main-scroll';

  isVisible = false;
  private removeTargetScrollListener?: () => void;
  private attachRetryTimeoutId: ReturnType<typeof setTimeout> | undefined;
  private retryCount = 0;
  private static readonly maxRetries = 8;
  private static readonly retryDelayMs = 100;

  constructor(
    @Inject(DOCUMENT) private readonly doc: Document,
    private readonly renderer: Renderer2,
    private readonly cdr: ChangeDetectorRef,
    private readonly hostRef: ElementRef<HTMLElement>
  ) {}

  ngAfterViewInit(): void {
    this.reparentHostToBody();
    this.tryAttachAndRetry();
  }

  ngOnDestroy(): void {
    if (this.attachRetryTimeoutId != null) {
      clearTimeout(this.attachRetryTimeoutId);
    }
    this.removeTargetScrollListener?.();
    const host = this.hostRef.nativeElement;
    if (host.parentNode === this.doc.body) {
      this.renderer.removeChild(this.doc.body, host);
    }
  }

  /**
   * `position:fixed` inside `<main class="overflow-auto">` is often clipped or not visible in the viewport.
   * Host on `body` keeps the FAB above the scroll pane (same pattern as modals).
   */
  private reparentHostToBody(): void {
    const host = this.hostRef.nativeElement;
    if (host.parentNode !== this.doc.body) {
      this.renderer.appendChild(this.doc.body, host);
    }
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.updateVisibility();
  }

  scrollToEnd(): void {
    const target = this.getTargetElement();
    const mainScroll = this.doc.getElementById(ScrollToTopButtonComponent.mainScrollId);
    if (this.direction === 'top') {
      if (target) target.scrollTo({ top: 0, behavior: 'smooth' });
      if (mainScroll) mainScroll.scrollTo({ top: 0, behavior: 'smooth' });
      this.doc.defaultView?.scrollTo({ top: 0, behavior: 'smooth' });
      this.setVisibility(false);
    } else {
      if (target) {
        target.scrollTo({ top: target.scrollHeight, behavior: 'smooth' });
      } else {
        const root = this.doc.documentElement;
        this.doc.defaultView?.scrollTo({ top: root.scrollHeight, behavior: 'smooth' });
      }
    }
  }

  private tryAttachAndRetry(): void {
    this.attachTargetScrollListener();
    this.updateVisibility();
    const target = this.getTargetElement();
    if (!target && this.targetId && this.retryCount < ScrollToTopButtonComponent.maxRetries) {
      this.retryCount++;
      this.attachRetryTimeoutId = setTimeout(() => {
        this.attachRetryTimeoutId = undefined;
        this.tryAttachAndRetry();
      }, ScrollToTopButtonComponent.retryDelayMs);
    }
  }

  private attachTargetScrollListener(): void {
    this.removeTargetScrollListener?.();
    const target = this.getTargetElement();
    const mainScroll = this.doc.getElementById(ScrollToTopButtonComponent.mainScrollId);
    const listeners: (() => void)[] = [];

    if (target) {
      listeners.push(this.renderer.listen(target, 'scroll', () => this.updateVisibility()));
    }
    if (mainScroll && mainScroll !== target) {
      listeners.push(this.renderer.listen(mainScroll, 'scroll', () => this.updateVisibility()));
    }

    this.removeTargetScrollListener = () => listeners.forEach((fn) => fn());
  }

  private getTargetElement(): HTMLElement | null {
    if (this.targetId) {
      return this.doc.getElementById(this.targetId);
    }
    return this.doc.getElementById(ScrollToTopButtonComponent.mainScrollId);
  }

  private updateVisibility(target?: HTMLElement | null): void {
    const scrollTarget = target ?? this.getTargetElement();
    if (this.direction === 'top') {
      const mainTop = scrollTarget ? scrollTarget.scrollTop : 0;
      const docEl = this.doc.documentElement;
      const body = this.doc.body;
      const windowTop = docEl.scrollTop || body.scrollTop || this.doc.defaultView?.scrollY || 0;
      this.setVisibility(Math.max(mainTop, windowTop) > this.threshold);
      return;
    }
    let fromBottom = 0;
    if (scrollTarget) {
      fromBottom = scrollTarget.scrollHeight - scrollTarget.scrollTop - scrollTarget.clientHeight;
    } else {
      const docEl = this.doc.documentElement;
      const y = this.doc.defaultView?.scrollY ?? 0;
      fromBottom = docEl.scrollHeight - y - (this.doc.defaultView?.innerHeight ?? 0);
    }
    this.setVisibility(fromBottom > this.threshold);
  }

  private setVisibility(isVisible: boolean): void {
    if (this.isVisible === isVisible) {
      return;
    }
    this.isVisible = isVisible;
    this.cdr.detectChanges();
  }
}
